"use client";

import { logEventAnalytics } from "@features/firebase";

export type TrackingEventPayload = Record<string, unknown>;

/**
 * GA DOM label attribute builder.
 *
 * Produces `data-tracking-eventname` and `data-tracking-event-key-*` attributes.
 * 浏览器里由 GTM 触发器读取 → 派发 `logbeacon:log` → 阿里云 SLS；
 * App WebView 里由原生壳读属性 → Firebase → GA4。
 *
 * @deprecated **冻结存量，禁止新增落点**（规范 §0，2026-08-07 决策）。
 * 现有落点保留原样长期运行，但**新代码一律只写 `Track.*` / `data-openreplay-label` / `data-or-*`**
 * —— 业务类数据两端只走 OpenReplay。
 *
 * 唯一例外是 Adjust / GA 自身有采集需求（归因、投放转化口径），那属于 Ask first，
 * 且要在 `docs/engineering/tracking/tracking-conventions.md` §0 的例外表补一行。
 *
 * 新增落点会被 `apps/token-seek/scripts/check-ga-freeze.mjs` 拦下（棘轮，总数只许降）。
 * 曝光改用 `data-or-view`，点击改用 `data-openreplay-label`（+ 需要参数时 `data-or-event`）。
 */
export function gaTrackingAttrs(
  eventName: string,
  params?: Record<string, string | number | boolean | undefined | null>,
): Record<string, string | undefined> {
  const attrs: Record<string, string | undefined> = {
    "data-tracking-eventname": eventName,
  };
  if (params) {
    for (const [key, value] of Object.entries(params)) {
      if (value != null && value !== "") {
        attrs[`data-tracking-event-key-${key}`] = String(value);
      }
    }
  }
  return attrs;
}

/**
 * Active GA event reporting (no OpenReplay).
 * `logEventAnalytics` → Firebase `logEvent("edgen_" + name)` → **GA4**（不碰 SLS）。
 *
 * @deprecated **冻结存量，禁止新增落点**（规范 §0，2026-08-07 决策）。理由同 {@link gaTrackingAttrs}。
 * 新代码用 `Track.event(name, params)` / `Track.view(name, params)`。
 *
 * 迁移中的域若必须两边都发，**只起一个出口同时发两条**（样板：
 * `src/screens/plan/planActivityAnalytics.ts` 的 `emitPlanEvent`），
 * 不许在调用点分别写 —— 出口不唯一 = 改一处漏一处。
 */
export function trackEvent(
  event: string,
  payload?: TrackingEventPayload,
): void {
  const normalized = payload
    ? Object.fromEntries(
        Object.entries(payload).filter(([, value]) => value !== undefined),
      )
    : undefined;
  logEventAnalytics(event, normalized);
}
